// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// mui
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

export interface AvailableLicenceCode {
  id: string;
  code: string;
  startDate?: string;
  productId: string;
  productName: string;
  isAllocated?: boolean;
}

export interface ClientReferralLeadDataValues {
  whoWillPay: string;
  discountCode: string;
  licenceCode?: string;
  consentCommunications: boolean;
  consentShareInfo: boolean;
}

export interface ClientReferralLeadDataPageProps {
  onBack?: () => void;
  onSubmit: (data: ClientReferralLeadDataValues) => void | Promise<void>;
  initialValues?: Partial<ClientReferralLeadDataValues>;
  isSubmitting?: boolean;
  showLicenceCodeOption?: boolean;
  availableLicenceCodes?: AvailableLicenceCode[];
  isSJP?: boolean;
}

type FieldName = 'whoWillPay' | 'consent' | 'licenceCode';

export const ClientReferralLeadDataPage = ({
  onBack,
  onSubmit,
  initialValues,
  isSubmitting = false,
  showLicenceCodeOption = false,
  availableLicenceCodes = [],
  isSJP = false,
}: ClientReferralLeadDataPageProps) => {
  const [whoWillPay, setWhoWillPay] = useState(initialValues?.whoWillPay || '');
  const [discountCode, setDiscountCode] = useState(
    initialValues?.discountCode || '',
  );
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedLicenceCode, setSelectedLicenceCode] = useState('');
  const [consentCommunications, setConsentCommunications] = useState(
    initialValues?.consentCommunications || false,
  );
  const [consentShareInfo, setConsentShareInfo] = useState(
    initialValues?.consentShareInfo || false,
  );
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});

  const products = useMemo(() => {
    const seen = new Map<string, string>();
    for (const code of availableLicenceCodes) {
      if (!seen.has(code.productId)) {
        seen.set(code.productId, code.productName);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [availableLicenceCodes]);

  const selectedCodeObj = useMemo(
    () => availableLicenceCodes.find((c) => c.code === selectedLicenceCode),
    [availableLicenceCodes, selectedLicenceCode],
  );

  const effectiveFilteredCodes = useMemo(
    () => availableLicenceCodes.filter((c) => c.productId === selectedProduct),
    [availableLicenceCodes, selectedProduct],
  );

  useEffect(() => {
    if (products.length === 1 && !selectedProduct) {
      setSelectedProduct(products[0].id);
    }
  }, [products, selectedProduct]);

  const isLicenceCodeMode = whoWillPay === 'licence-code';
  const licenceCodeValid = !isLicenceCodeMode || selectedLicenceCode.length > 0;
  const isValid =
    whoWillPay.length > 0 &&
    consentCommunications &&
    consentShareInfo &&
    licenceCodeValid;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const newErrors: Partial<Record<FieldName, string>> = {};
      if (!whoWillPay) newErrors.whoWillPay = 'Please select a payment option.';
      if (isLicenceCodeMode && !selectedLicenceCode)
        newErrors.licenceCode = 'Please select a seat code.';
      if (!consentCommunications || !consentShareInfo)
        newErrors.consent = 'Both consent checkboxes are required.';
      setErrors(newErrors);

      if (Object.keys(newErrors).length > 0) return;

      await onSubmit({
        whoWillPay,
        discountCode: isLicenceCodeMode ? '' : discountCode.trim(),
        licenceCode: isLicenceCodeMode ? selectedLicenceCode : undefined,
        consentCommunications,
        consentShareInfo,
      });
    },
    [
      whoWillPay,
      discountCode,
      selectedLicenceCode,
      isLicenceCodeMode,
      consentCommunications,
      consentShareInfo,
      onSubmit,
    ],
  );

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title='Client Referral - Referral Details'
            description='Tell us a bit more about who will pay for the service, and ensure client consent is present.'
          />
        }
        contentSlot={
          <Box component='form' onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <FormControl required error={!!errors.whoWillPay}>
                <FormLabel>Who will pay for the service?</FormLabel>
                <RadioGroup
                  value={whoWillPay}
                  onChange={(e) => {
                    setWhoWillPay(e.target.value);
                    if (errors.whoWillPay) {
                      setErrors((prev) => ({ ...prev, whoWillPay: undefined }));
                    }
                  }}
                >
                  <FormControlLabel
                    value='partner-paying-fee'
                    control={<Radio />}
                    label='I, the referring advisor, agree to pay the fee for the Concierge Service'
                    disabled={isSubmitting}
                  />
                  <FormControlLabel
                    value='client-paying-fee'
                    control={<Radio />}
                    label='My client will be paying the fee for this service and is aware of the cost'
                    disabled={isSubmitting}
                  />
                  {showLicenceCodeOption && (
                    <FormControlLabel
                      value='licence-code'
                      control={<Radio />}
                      label='Allocate a seat code'
                      disabled={isSubmitting}
                    />
                  )}
                </RadioGroup>
                {errors.whoWillPay && (
                  <FormHelperText>{errors.whoWillPay}</FormHelperText>
                )}
              </FormControl>

              {isLicenceCodeMode ? (
                <Stack spacing={2}>
                  <TextField
                    select
                    label='Product'
                    value={selectedProduct}
                    onChange={(e) => {
                      setSelectedProduct(e.target.value);
                      setSelectedLicenceCode('');
                    }}
                    fullWidth
                    disabled={isSubmitting}
                  >
                    {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <FormControl required error={!!errors.licenceCode}>
                    <Autocomplete
                      options={effectiveFilteredCodes}
                      getOptionLabel={(c) => c.code}
                      isOptionEqualToValue={(a, b) => a.code === b.code}
                      value={selectedCodeObj ?? null}
                      onChange={(_, value) => {
                        setSelectedLicenceCode(value?.code ?? '');
                        if (errors.licenceCode) {
                          setErrors((prev) => ({
                            ...prev,
                            licenceCode: undefined,
                          }));
                        }
                      }}
                      disabled={isSubmitting || !selectedProduct}
                      fullWidth
                      renderOption={(props, c) => (
                        <li {...props} key={c.id}>
                          {c.code}
                          {c.isAllocated ? ' (allocated)' : ''}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Seat Code'
                          placeholder='Type to search'
                          error={!!errors.licenceCode}
                          helperText={errors.licenceCode}
                        />
                      )}
                    />
                  </FormControl>

                  {selectedCodeObj?.isAllocated && (
                    <Alert severity='warning'>
                      This code has already been used to invite a client. If you
                      proceed, whichever client registers first will consume the
                      code.
                    </Alert>
                  )}
                </Stack>
              ) : (
                <TextField
                  label='Discount Code'
                  placeholder='KENI2026'
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  fullWidth
                  disabled={isSubmitting}
                />
              )}

              <FormControl
                component='fieldset'
                required
                error={!!errors.consent}
              >
                <FormLabel component='legend'>
                  GDPR Notice and Consent
                </FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={consentCommunications}
                        onChange={(e) => {
                          setConsentCommunications(e.target.checked);
                          if (errors.consent) {
                            setErrors((prev) => ({
                              ...prev,
                              consent: undefined,
                            }));
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    }
                    label='I have received consent from the referred client to receive communications from KareHero.'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={consentShareInfo}
                        onChange={(e) => {
                          setConsentShareInfo(e.target.checked);
                          if (errors.consent) {
                            setErrors((prev) => ({
                              ...prev,
                              consent: undefined,
                            }));
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    }
                    label='I have received consent from the referred client that they would like KareHero to share relevant information about their care journey with me, their referring partner.'
                  />
                </FormGroup>
                {errors.consent && (
                  <FormHelperText>{errors.consent}</FormHelperText>
                )}
              </FormControl>

              {isSJP ? (
                <Alert severity='info'>
                  <AlertTitle>
                    <Typography variant='subtitle2'>
                      KareHero Care Concierge Fees As Follows
                    </Typography>
                  </AlertTitle>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Full Service: &pound;500 + VAT</li>
                    <li>Fund-Only Service: &pound;250 + VAT</li>
                  </ul>
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    Both cover 12 months of service access with optional
                    renewal.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity='info'>
                  <Typography variant='body2'>
                    Speak to your dedicated KareHero Sales Representative for a
                    quote.{' '}
                    <Link
                      href='https://meetings-eu1.hubspot.com/bheven/meet-karehero-for-financial-advisers'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Book a meeting
                    </Link>
                  </Typography>
                </Alert>
              )}

              <Button
                type='submit'
                variant='contained'
                fullWidth
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? 'Submitting…' : 'Next'}
              </Button>
            </Stack>
          </Box>
        }
      />
    </ModalPageTemplate>
  );
};

ClientReferralLeadDataPage.displayName = 'ClientReferralLeadDataPage';
export default ClientReferralLeadDataPage;
